import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { DashboardOverview } from '../../../store/reducer/DashboardReducer';
import {
  PiCheckSquareOffsetLight,
  PiBriefcaseLight,
  PiProjectorScreenChartLight,
  PiFileMagnifyingGlassLight,
  PiArrowRightLight,
} from 'react-icons/pi';

interface QuickMetricsProps {
  overview: DashboardOverview | null;
}

interface MetricDef {
  title: string;
  value: number;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  section: string;
  route: string;
  accent?: string;
}

const QuickMetrics: React.FC<QuickMetricsProps> = ({ overview }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  const metrics: MetricDef[] = [
    {
      title: t('Open Tasks'),
      value: overview?.tasks_open ?? 0,
      icon: <PiCheckSquareOffsetLight className="w-5 h-5" />,
      iconBg: 'bg-violet-50 dark:bg-violet-900/30',
      iconColor: 'text-violet-600 dark:text-violet-400',
      section: 'tasks',
      route: '/tasks',
      accent: overview && overview.tasks_overdue > 0 ? 'ring-1 ring-red-200 dark:ring-red-800/50' : undefined,
    },
    {
      title: t('Active Matters'),
      value: overview?.matters ?? 0,
      icon: <PiBriefcaseLight className="w-5 h-5" />,
      iconBg: 'bg-blue-50 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      section: 'matters',
      route: '/matters',
    },
    {
      title: t('Active Projects'),
      value: overview?.projects_active ?? 0,
      icon: <PiProjectorScreenChartLight className="w-5 h-5" />,
      iconBg: 'bg-teal-50 dark:bg-teal-900/30',
      iconColor: 'text-teal-600 dark:text-teal-400',
      section: 'projects',
      route: '/projects',
    },
    {
      title: t('Active Reviews'),
      value: overview?.reviews_active ?? 0,
      icon: <PiFileMagnifyingGlassLight className="w-5 h-5" />,
      iconBg: 'bg-amber-50 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
      section: 'review',
      route: '/reviews',
    },
  ];

  const visibleMetrics = metrics.filter(m => hasSectionAccess(m.section));

  const gridCols = visibleMetrics.length <= 2
    ? 'grid-cols-2'
    : visibleMetrics.length === 3
      ? 'grid-cols-3'
      : 'grid-cols-2 lg:grid-cols-4';

  if (!overview) {
    return (
      <div className={`grid ${gridCols} gap-3 h-full`}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 p-4 animate-fadePulse">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="h-7 w-12 bg-gray-200 dark:bg-gray-700 rounded mb-1.5" />
            <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`grid ${gridCols} gap-3 h-full`}>
      {visibleMetrics.map((metric, index) => (
        <div
          key={metric.title}
          onClick={() => history.push(metric.route)}
          className={`group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80
            p-4 cursor-pointer flex flex-col justify-between
            hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600
            transition-all duration-300 animate-fade-in
            ${metric.accent || ''}`}
          style={{ animationDelay: `${index * 60}ms` }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${metric.iconBg} ${metric.iconColor}`}>
              {metric.icon}
            </div>
            <PiArrowRightLight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div>
            <div className="text-2xl text-gray-900 dark:text-gray-50 mb-0.5">
              {metric.value.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {metric.title}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default QuickMetrics;
